// CSeatBadges — 4 家風位徽章 + 莊家標
// 從 game-scene.ts 抽出，把整組「貼在桌面上、indicate 各家位置」的視覺元素打包。
//
// 設計：整組視為一個快取/重建單位。預覽模式按 Page Down 切莊家時，由 controller
// 直接呼 render(...) 重畫整組。本元件持有所有建立的 GameObject，render() 內部會先
// destroy 舊物件再建立新的，呼叫端不需自己管理生命週期。
//
// 風字徽章用 table-text spritesheet 直接畫（無底色方框）；莊家標用 icon-dealer.png。
//
// === 使用圖片 ===
// - public/assets/ui/table-text.png（key: TABLE_TEXT_KEY，4×4 spritesheet）
//     row 1 normal（半透明）/ row 2 active（高亮、不透明）東/南/西/北
// - public/assets/ui/icon-dealer.png（key: ICON_DEALER_KEY）— 莊家「莊」字徽章
// 聽牌「聽」持續標已移到 CActionIcons（顯示在各家手牌區、比照原版 mListenSprite），本元件不再處理。

import type Phaser from 'phaser';
import { glow } from './motion';

import type { TSeat, TWind } from '@core/types';
import {
  ICON_DEALER_KEY,
  TABLE_TEXT_KEY,
  TableTextFrame,
  type TTableTextFrame,
} from '@scenes/boot-scene';

import { isSeatWindActiveForTurn } from './turn-indicator-presentation';
import { UIDepth } from './ui-depths';

// ---- 4 家風位徽章（純風字圖，無底色方框） ----
// depth 介於桌面背景（border = -99）與牌面 sprite（預設 0）之間 → 風字像「印」在桌面上、被牌覆蓋
const WIND_BADGE_TEXT_DEPTH = UIDepth.WindBadge; // 風字圖（高於桌面、低於所有 tile sprite）
const WIND_BADGE_TEXT_SCALE = 1.0; // 風字圖縮放（54×54 sprite 直接顯示）
const WIND_BADGE_TEXT_ALPHA = 0.5; // 風字圖透明度（半透明融入桌面）

// TWind → table-text spritesheet row 1 (normal) frame index
const WIND_BADGE_TEXT_FRAME: Record<TWind, TTableTextFrame> = {
  east: TableTextFrame.EastNormal,
  south: TableTextFrame.SouthNormal,
  west: TableTextFrame.WestNormal,
  north: TableTextFrame.NorthNormal,
};

// TWind → table-text spritesheet row 2 (active) frame index（高亮樣式）
const WIND_BADGE_TEXT_FRAME_ACTIVE: Record<TWind, TTableTextFrame> = {
  east: TableTextFrame.EastActive,
  south: TableTextFrame.SouthActive,
  west: TableTextFrame.WestActive,
  north: TableTextFrame.NorthActive,
};

// 每家風位徽章的中心座標
// export：回合指示手勢（CTurnIndicatorHand）要相對徽章定位，共用同一份座標來源
export const SEAT_BADGE_SPOTS: Record<TSeat, { x: number; y: number }> = {
  0: { x: 640, y: 560 }, // 玩家（下）
  1: { x: 1100, y: 360 }, // 右家
  2: { x: 640, y: 150 }, // 對家（上）
  3: { x: 180, y: 360 }, // 左家
};

// ---- 莊家標 (icon-dealer) ----
// 放在「該家風位徽章」的指定方向偏移處（不再用絕對座標）：
//   - 自家(下, seat 0) / 對家(上, seat 2) → 風字右側
//   - 下家(右, seat 1) / 上家(左, seat 3) → 風字下方
const DEALER_ICON_OFFSET = 50; // 莊家 icon 中心相對風字徽章中心的距離（px）
const DEALER_ICON_SCALE = 0.6; // 莊家 icon 縮放
const DEALER_ICON_DEPTH = UIDepth.DealerIcon; // 莊家 icon（高於風位徽章）

export type TSeatBadgesRenderParams = {
  /** 每家當前風位（用以決定徽章顯示哪個風字 frame）。 */
  readonly seatWinds: Record<TSeat, TWind>;
  /** 莊家座位（會額外掛 icon-dealer）。 */
  readonly dealerSeat: TSeat;
  /**
   * 當前回合要用 active（row 2 高亮）樣式的座位；null = 全部用 normal。
   * 正式牌局與預覽模式共用，取代原本可見的回合指示手勢。
   */
  readonly activeSeat: TSeat | null;
  readonly playerSeat?: TSeat;
};

/**
 * 4 家風位徽章 + 莊家 icon + 自家 label 的 UI 元件。
 * 整組 destroy/rebuild — 預覽模式切換莊家時直接呼 render() 重建。
 */
export class CSeatBadges {
  private readonly scene: Phaser.Scene;
  private objects: Phaser.GameObjects.GameObject[] = [];
  private lastActiveSeat: TSeat | null = null;
  private turnGlow: Phaser.GameObjects.Graphics | undefined;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  /** 重建整組徽章；舊物件會先被 destroy。 */
  render(params: TSeatBadgesRenderParams): void {
    const changed = this.lastActiveSeat !== params.activeSeat;
    this.lastActiveSeat = params.activeSeat;
    if (changed) {
      this.turnGlow?.destroy();
      this.turnGlow = undefined;
    }
    this.clear();
    for (let s = 0; s < 4; s++) {
      const seat = s as TSeat;
      const spot = SEAT_BADGE_SPOTS[seat];
      const wind = params.seatWinds[seat];
      const badge = this.createWindBadge(
        spot.x,
        spot.y,
        wind,
        isSeatWindActiveForTurn(seat, params.activeSeat)
      );
      if (changed && seat === params.activeSeat && seat === (params.playerSeat ?? 0)) {
        this.turnGlow = glow(this.scene, spot.x, spot.y, badge.displayWidth, badge.displayHeight);
        this.turnGlow?.setDepth(WIND_BADGE_TEXT_DEPTH + 1);
      }

      if (seat === params.dealerSeat && this.scene.textures.exists(ICON_DEALER_KEY)) {
        const iconSpot = getDealerIconSpot(seat);
        const icon = this.scene.add
          .image(iconSpot.x, iconSpot.y, ICON_DEALER_KEY)
          .setScale(DEALER_ICON_SCALE)
          .setDepth(DEALER_ICON_DEPTH);
        this.objects.push(icon);
      }
    }
  }

  /** 清掉所有徽章物件（render() 內部會呼叫；外部一般不需要）。 */
  clear(): void {
    for (const obj of this.objects) obj.destroy();
    this.objects = [];
  }

  /** 風位徽章：純風字圖（table-text spritesheet，無底色方框）。 */
  private createWindBadge(
    x: number,
    y: number,
    wind: TWind,
    active: boolean
  ): Phaser.GameObjects.Image {
    // active = true 時換成 row 2 的橘紅高亮樣式 + 全不透明（強調當前輪到誰）
    const frame = active ? WIND_BADGE_TEXT_FRAME_ACTIVE[wind] : WIND_BADGE_TEXT_FRAME[wind];
    const alpha = active ? 1 : WIND_BADGE_TEXT_ALPHA;
    const windImg = this.scene.add
      .image(x, y, TABLE_TEXT_KEY, frame)
      .setScale(WIND_BADGE_TEXT_SCALE)
      .setAlpha(alpha)
      .setDepth(WIND_BADGE_TEXT_DEPTH);
    this.objects.push(windImg);
    return windImg;
  }
}

/**
 * 莊家 icon 應放置的座標（相對該家風字徽章中心的方向偏移）。
 * 0=自家(下) / 2=對家(上) → 右側；1=下家(右) / 3=上家(左) → 下方。
 */
function getDealerIconSpot(seat: TSeat): { x: number; y: number } {
  const badge = SEAT_BADGE_SPOTS[seat];
  if (seat === 0 || seat === 2) {
    return { x: badge.x + DEALER_ICON_OFFSET, y: badge.y };
  }
  return { x: badge.x, y: badge.y + DEALER_ICON_OFFSET };
}
